import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from 'firebase/firestore';

import { screenType, status } from '../../../../constants/enum';
import { deleteFile, uploadFile } from '../../../aws/s3';
import { db } from '../../config';
import { userSnapshot, usersRef } from '../user';

import type {
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  QueryDocumentSnapshot
} from 'firebase/firestore';

export const companiesRef = collection(db, 'companies');

export const companyDocument = (companyId: string): DocumentReference =>
  doc(companiesRef, companyId);

export const companySnapshot = async (
  companyId: string
): Promise<DocumentSnapshot> => await getDoc(companyDocument(companyId));

export const companyFindByUserId = async (
  uid: string
): Promise<DocumentSnapshot> => {
  const user = await userSnapshot(uid);
  const company = await companySnapshot(user.data()?.companyId as string);
  return company;
};

export const getCompanyById = async (
  companyId: string
): Promise<DocumentData | null> => {
  const snapshot = await getDoc(doc(db, 'companies', companyId));
  return snapshot.exists() ? snapshot.data() : null;
};

export const addPublisherCompany = async (
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  // 更新するcompanyiesコレクションのデータ
  const companyData = {
    address: String(watch('address')) || null,
    capital: Number(watch('capital')) || null,
    city: String(watch('city')),
    created: new Date(),
    employees: Number(watch('numbers')) || null,
    foundationYear: Number(watch('yearEstablished')) || null,
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    listedFlg: Boolean(watch('listing')),
    name: watch('companyName') as string,
    prefecture: Number(watch('prefecture')),
    president: (watch('leaderName') as string) || null,
    pressReleases:
      ([
        watch('resources_0') || null,
        watch('resources_1') || null,
        watch('resources_2') || null,
        watch('resources_3') || null,
        watch('resources_4') || null
      ] as string[]) || null,
    sales: Number(watch('sales')) || null,
    snsAccount:
      ([
        watch('sns_0') || null,
        watch('sns_1') || null,
        watch('sns_2') || null,
        watch('sns_3') || null,
        watch('sns_4') || null
      ] as string[]) || null,
    status: status.notChecked,
    type: screenType.publisher,
    updated: new Date(),
    url: watch('companyUrl') as string,
    zipcode: String(watch('zipcode'))
  };
  const addCompanyDoc = await addDoc(companiesRef, companyData);
  // 登録したcompanyドキュメントを取得
  const companyDoc = doc(companiesRef, addCompanyDoc.id);
  // Companyのドキュメントのidを追加
  await updateDoc(companyDoc, {
    companyId: companyDoc.id
  });

  return companyDoc;
};

export const editPublisherCompany = async (
  uid: string,
  watch: (arg0: string) => any,
  fileBg: FileList | null,
  fileLogo: FileList | null
): Promise<void> => {
  const q = query(usersRef, where('uid', '==', uid));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);
  let companyId = '';
  let oldFilebg = '';
  let oldFileLogo = '';
  querySnapshot.docs.forEach(
    (document: QueryDocumentSnapshot<DocumentData>): void => {
      companyId = document.data().companyId as string;
      oldFilebg = document.data().bgImgId as string;
      oldFileLogo = document.data().logoId as string;
    }
  );

  // ファイル名を取得
  const fileNameBg = fileBg !== null ? fileBg[0].name : '';
  const fileNameLogo = fileLogo !== null ? fileLogo[0].name : '';
  const bg =
    fileNameBg !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameBg}`
      : null;
  const logo =
    fileNameLogo !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameLogo}`
      : null;

  // 更新するcompanyiesコレクションのデータ
  let companyData = {
    address:
      watch('address') !== undefined && watch('address') !== null
        ? String(watch('address'))
        : null,
    capital: Number(watch('capital')) || null,
    city: String(watch('city')),
    employees: Number(watch('employees')) || null,
    foundationYear: Number(watch('foundationYear')) || null,
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    introduction: String(watch('introduction')),
    listedFlg: Boolean(watch('listedFlg')),
    name: (watch('companyName') as string) || null,
    prefecture: Number(watch('prefecture')),
    president: (watch('president') as string) || null,
    pressReleases:
      ([
        watch('resources_0') || null,
        watch('resources_1') || null,
        watch('resources_2') || null,
        watch('resources_3') || null,
        watch('resources_4') || null
      ] as string[]) || null,
    sales: Number(watch('sales')) || null,
    snsAccount:
      ([
        watch('sns_0') || null,
        watch('sns_1') || null,
        watch('sns_2') || null,
        watch('sns_3') || null,
        watch('sns_4') || null
      ] as string[]) || null,
    type: screenType.publisher,
    updated: new Date(),
    url: watch('companyUrl') as string,
    zipcode: String(watch('zipcode'))
  };

  // 古い背景画像データは削除
  if (bg !== null) {
    uploadFile(fileBg, bg);
    companyData = { ...companyData, ...{ bgImgId: bg } };
    if (oldFilebg) {
      deleteFile(oldFilebg);
    }
  }

  // 古いロゴデータは削除
  if (logo !== null) {
    uploadFile(fileLogo, logo);
    companyData = { ...companyData, ...{ logoId: logo } };
    if (oldFileLogo) {
      deleteFile(oldFileLogo);
    }
  }

  const companyDoc = doc(companiesRef, companyId);
  await updateDoc(companyDoc, companyData);
};

export const addCompanyFromAgent = async (
  watch: (arg0: string) => any,
  agentCompanyId: string
): Promise<string> => {
  const companyData = {
    agentFlg: true,
    agentLists: [{ companyId: agentCompanyId }],
    created: new Date(),
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    name: watch('companyName') as string,
    status: status.approved,
    type: screenType.client,
    updated: new Date(),
    url: watch('companyUrl') as string
  };
  const addCompanyDoc = await addDoc(companiesRef, companyData);
  // 登録したcompanyドキュメントを取得
  const companyDoc = doc(companiesRef, addCompanyDoc.id);
  // Companyのドキュメントのidを追加
  await updateDoc(companyDoc, {
    companyId: companyDoc.id
  });
  // 代理店のclientListsに広告主IDを追加
  const agentCompanyDoc = doc(companiesRef, agentCompanyId);
  await updateDoc(agentCompanyDoc, {
    clientLists: arrayUnion({ companyId: companyDoc.id }),
    updated: new Date()
  });
  return companyDoc.id;
};

export const editCompanyFromAgent = async (
  watch: (arg0: string) => any,
  companyId: string
): Promise<void> => {
  const companyData = {
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    name: watch('companyName') as string,
    updated: new Date(),
    url: watch('companyUrl') as string
  };
  // 登録したcompanyドキュメントを取得
  const companyDoc = doc(companiesRef, companyId);
  // Companyのドキュメントのidを追加
  await updateDoc(companyDoc, companyData);
};

export const addClientCompany = async (
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  // 更新するcompanyiesコレクションのデータ
  const companyData = {
    created: new Date(),
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    name: watch('companyName') as string,
    status: status.notChecked,
    type: screenType.client,
    updated: new Date(),
    url: watch('companyUrl') as string
  };
  const addCompanyDoc = await addDoc(companiesRef, companyData);
  // 登録したcompanyドキュメントを取得
  const companyDoc = doc(companiesRef, addCompanyDoc.id);
  // Companyのドキュメントのidを追加
  await updateDoc(companyDoc, {
    companyId: companyDoc.id
  });

  return companyDoc;
};

export const addAgentCompany = async (
  watch: (arg0: string) => any
): Promise<DocumentReference<DocumentData>> => {
  // 更新するcompanyiesコレクションのデータ
  const companyData = {
    created: new Date(),
    industry: (watch('industry') as number) || null,
    industryOther: (watch('industryOther') as string) || null,
    name: watch('companyName') as string,
    status: status.notChecked,
    type: screenType.agent,
    updated: new Date(),
    url: watch('companyUrl') as string
  };
  const addCompanyDoc = await addDoc(companiesRef, companyData);
  // 登録したcompanyドキュメントを取得
  const companyDoc = doc(companiesRef, addCompanyDoc.id);
  // Companyのドキュメントのidを追加
  await updateDoc(companyDoc, {
    companyId: companyDoc.id
  });

  return companyDoc;
};

export const editClientCompanyFromAgent = async (
  companyId: string,
  watch: (arg0: string) => any,
  file: FileList | null = null,
  editFlg: boolean
): Promise<void> => {
  const fileName = file !== null ? file[0].name : '';
  const date = new Date();
  const logo =
    fileName !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileName}`
      : null;

  let companyData;
  if (editFlg && logo === null) {
    companyData = {
      address:
        watch('address') !== undefined && watch('address') !== null
          ? String(watch('address'))
          : null,
      city: String(watch('city')),
      employees: Number(watch('employees')) || null,
      fiscalMonth: Number(watch('fiscalMonth')),
      foundationYear: Number(watch('foundationYear')) || null,
      introduction:
        String(watch('introduction')) !== 'undefined'
          ? String(watch('introduction'))
          : null,
      listedFlg:
        (watch('listedFlg') as string | boolean | null) === null
          ? null
          : (watch('listedFlg') as string | boolean | null) === 'true'
          ? true
          : (watch('listedFlg') as string | boolean | null) === 'false'
          ? false
          : (watch('listedFlg') as string | boolean | null),
      logo: null,
      prefecture: Number(watch('prefecture')),
      pressReleases:
        ([
          watch('resources_0') || null,
          watch('resources_1') || null,
          watch('resources_2') || null,
          watch('resources_3') || null,
          watch('resources_4') || null
        ] as string[]) || null,
      product:
        (watch('products') as Array<{
          productName: string | null;
          productUrl: string | null;
        }>) ?? null,
      sales: Number(watch('sales')),
      snsAccount:
        ([
          watch('sns_0') || null,
          watch('sns_1') || null,
          watch('sns_2') || null,
          watch('sns_3') || null,
          watch('sns_4') || null
        ] as string[]) || null,
      updated: date,
      zipcode: String(watch('zipcode'))
    };
  } else {
    companyData = {
      address:
        watch('address') !== undefined && watch('address') !== null
          ? String(watch('address'))
          : null,
      city: String(watch('city')),
      employees: Number(watch('employees')) || null,
      fiscalMonth: Number(watch('fiscalMonth')) || null,
      foundationYear: Number(watch('foundationYear')) || null,
      introduction:
        String(watch('introduction')) !== 'undefined'
          ? String(watch('introduction'))
          : null,
      listedFlg:
        (watch('listedFlg') as string | boolean | null) === null
          ? null
          : (watch('listedFlg') as string | boolean | null) === 'true'
          ? true
          : (watch('listedFlg') as string | boolean | null) === 'false'
          ? false
          : (watch('listedFlg') as string | boolean | null),
      logoId: logo,
      prefecture: Number(watch('prefecture')),
      pressReleases:
        ([
          watch('resources_0') || null,
          watch('resources_1') || null,
          watch('resources_2') || null,
          watch('resources_3') || null,
          watch('resources_4') || null
        ] as string[]) || null,
      product:
        (watch('products') as Array<{
          productName: string | null;
          productUrl: string | null;
        }>) ?? null,
      sales: Number(watch('sales')) || null,
      snsAccount:
        ([
          watch('sns_0') || null,
          watch('sns_1') || null,
          watch('sns_2') || null,
          watch('sns_3') || null,
          watch('sns_4') || null
        ] as string[]) || null,
      updated: date,
      zipcode: String(watch('zipcode'))
    };
  }
  if (logo !== null) uploadFile(file, logo);
  const companyDoc = doc(companiesRef, companyId);
  await updateDoc(companyDoc, companyData);
};

export const editClientCompany = async (
  uid: string,
  watch: (arg0: string) => any,
  fileBg: FileList | null,
  fileLogo: FileList | null,
  type: string | null
): Promise<void> => {
  const q = query(usersRef, where('uid', '==', uid));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);
  let companyId = '';
  let oldFilebg = '';
  let oldFileLogo = '';
  querySnapshot.docs.forEach(
    (document: QueryDocumentSnapshot<DocumentData>): void => {
      companyId = document.data().companyId as string;
      oldFilebg = document.data().bgImgId as string;
      oldFileLogo = document.data().logoId as string;
    }
  );
  const fileNameBg = fileBg !== null ? fileBg[0].name : '';
  const fileNameLogo = fileLogo !== null ? fileLogo[0].name : '';
  const date = new Date();
  const bg =
    fileNameBg !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameBg}`
      : null;
  const logo =
    fileNameLogo !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameLogo}`
      : null;

  let companyData = {
    address:
      watch('address') !== undefined && watch('address') !== null
        ? String(watch('address'))
        : null,
    agentFlg:
      Boolean(watch('agentFlg') === 'true') || (watch('agentFlg') as boolean),
    agentLists:
      watch('agentLists') !== 0
        ? [{ companyId: String(watch('agentLists')) }]
        : null,
    city: String(watch('city')),
    employees: Number(watch('employees')) || null,
    fiscalMonth: Number(watch('fiscalMonth')) || null,
    foundationYear: Number(watch('foundationYear')) || null,
    introduction: String(watch('introduction')),
    listedFlg:
      (watch('listedFlg') as string | boolean | null) === null
        ? null
        : (watch('listedFlg') as string | boolean | null) === 'true'
        ? true
        : (watch('listedFlg') as string | boolean | null) === 'false'
        ? false
        : (watch('listedFlg') as string | boolean | null),
    prefecture: Number(watch('prefecture')),
    pressReleases:
      ([
        watch('resources_0') || null,
        watch('resources_1') || null,
        watch('resources_2') || null,
        watch('resources_3') || null,
        watch('resources_4') || null
      ] as string[]) || null,
    product: watch('products') as Array<{
      productNames: string | null;
      productUrls: string | null;
    }>,
    sales: Number(watch('sales')) || null,
    snsAccount:
      ([
        watch('sns_0') || null,
        watch('sns_1') || null,
        watch('sns_2') || null,
        watch('sns_3') || null,
        watch('sns_4') || null
      ] as string[]) || null,
    updated: date,
    zipcode: String(watch('zipcode'))
  };

  if (logo !== null) {
    uploadFile(fileLogo, logo);
    companyData = {
      ...companyData,
      ...{
        logoId: logo
      }
    };
  }

  // Mypageでの編集の場合
  if (type === 'edit') {
    if (bg !== null) {
      uploadFile(fileBg, bg);
      companyData = { ...companyData, ...{ bgImgId: bg } };
      if (oldFilebg) {
        deleteFile(oldFilebg);
      }
    }

    // 古いロゴは削除
    if (logo !== null && oldFileLogo) {
      deleteFile(oldFileLogo);
    }

    companyData = {
      ...companyData,
      ...{
        industry: (watch('industry') as number) || null,
        industryOther: (watch('industryOther') as string) || null,
        name: (watch('companyName') as string) || null,
        url: (watch('companyUrl') as string) || null
      }
    };
  }

  const companyDoc = doc(companiesRef, companyId);
  await updateDoc(companyDoc, companyData);
};

export const editAgentCompany = async (
  uid: string,
  watch: (arg0: string) => any,
  fileBg: FileList | null,
  fileLogo: FileList | null,
  type: string | null
): Promise<void> => {
  const q = query(usersRef, where('uid', '==', uid));
  // 一旦クエリで取得する
  const querySnapshot = await getDocs(q);
  let companyId = '';
  let oldFilebg = '';
  let oldFileLogo = '';
  querySnapshot.docs.forEach(
    (document: QueryDocumentSnapshot<DocumentData>): void => {
      companyId = document.data().companyId as string;
      oldFilebg = document.data().bgImgId as string;
      oldFileLogo = document.data().logoId as string;
    }
  );
  const fileNameBg = fileBg !== null ? fileBg[0].name : '';
  const fileNameLogo = fileLogo !== null ? fileLogo[0].name : '';
  const date = new Date();
  const bg =
    fileNameBg !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameBg}`
      : null;
  const logo =
    fileNameLogo !== ''
      ? `companies/${companyId}/profile/${Date.now()}_${fileNameLogo}`
      : null;

  let companyData = {
    address:
      watch('address') !== undefined && watch('address') !== null
        ? String(watch('address'))
        : null,
    capital: Number(watch('capital')) || null,
    city: String(watch('city')),
    employees: Number(watch('employees')) || null,
    fiscalMonth: Number(watch('fiscalMonth')) || null,
    foundationYear: Number(watch('foundationYear')) || null,
    introduction: String(watch('introduction')),
    listedFlg:
      (watch('listedFlg') as string | boolean | null) === null
        ? null
        : (watch('listedFlg') as string | boolean | null) === 'true'
        ? true
        : (watch('listedFlg') as string | boolean | null) === 'false'
        ? false
        : (watch('listedFlg') as string | boolean | null),
    prefecture: Number(watch('prefecture')),
    president: String(watch('representative')) || null,
    pressReleases:
      ([
        watch('resources_0') || null,
        watch('resources_1') || null,
        watch('resources_2') || null,
        watch('resources_3') || null,
        watch('resources_4') || null
      ] as string[]) || null,
    sales: Number(watch('sales')) || null,
    snsAccount:
      ([
        watch('sns_0') || null,
        watch('sns_1') || null,
        watch('sns_2') || null,
        watch('sns_3') || null,
        watch('sns_4') || null
      ] as string[]) || null,
    updated: date,
    zipcode: String(watch('zipcode'))
  };

  if (logo !== null) {
    uploadFile(fileLogo, logo);
    companyData = {
      ...companyData,
      ...{
        logoId: logo
      }
    };
  }

  // Mypageでの編集の場合
  if (type === 'edit') {
    if (bg !== null) {
      uploadFile(fileBg, bg);
      companyData = { ...companyData, ...{ bgImgId: bg } };
      if (oldFilebg) {
        deleteFile(oldFilebg);
      }
    }

    // 古いロゴは削除
    if (logo !== null && oldFileLogo) {
      deleteFile(oldFileLogo);
    }

    companyData = {
      ...companyData,
      ...{
        industry: (watch('industry') as number) || null,
        industryOther: (watch('industryOther') as string) || null,
        name: (watch('companyName') as string) || null,
        url: (watch('companyUrl') as string) || null
      }
    };
  }

  const companyDoc = doc(companiesRef, companyId);
  await updateDoc(companyDoc, companyData);
};

export const getAgent = async (
  type: number
): Promise<Array<{ value: string; option: string }>> => {
  const q = query(companiesRef, where('type', '==', type));
  const querySnapshot = await getDocs(q);
  const agentData: Array<{ value: string; option: string }> = [];
  querySnapshot.forEach((docs) => {
    const data = {
      option: String(docs.data().name),
      value: String(docs.data().companyId)
    };
    agentData.push(data);
  });
  return agentData;
};

export const getAgentFromSearch = async (
  companyId: string
): Promise<Array<{ value: string; option: string }>> => {
  const q = query(companiesRef, where('agentLists', '==', [{ companyId }]));
  const querySnapshot = await getDocs(q);
  const agentData: Array<{ value: string; option: string }> = [];
  querySnapshot.forEach((docs) => {
    const data = {
      option: String(docs.data().name),
      value: String(docs.data().companyId)
    };
    agentData.push(data);
  });
  return agentData;
};

export const bgImgUpload = async (
  userSnap: DocumentSnapshot<DocumentData>,
  s3File: FileList | null
): Promise<void> => {
  const ownCompanyId = userSnap.data()?.companyId as string;
  const companyDoc = doc(companiesRef, ownCompanyId);
  const fileName = s3File !== null ? s3File[0].name : '';
  const companySnap = await getDoc(companyDoc);
  const oldFile = companySnap.data()?.bgImgId as string;

  const backImage = `companies/${ownCompanyId}/profile/${Date.now()}_${fileName}`;
  const companyData = {
    bgImgId: backImage,
    updated: new Date()
  };
  await updateDoc(companyDoc, companyData);
  uploadFile(s3File, backImage);
  if (oldFile) {
    deleteFile(oldFile);
  }
};
